import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Loader2,
  Search,
  LayoutGrid,
  List,
  Heart,
  BadgeCheck,
  Lock,
  Clock,
} from 'lucide-react'
import {
  browseApps,
  listFavoriteApps,
  listCategories,
  favoriteApp,
  unfavoriteApp,
  requestAccess,
  startApp,
  stopApp,
  rebuildApp,
  type BrowseApp,
} from '../lib/api'
import { buildCategoryConfig, type CategoryStyle } from '../lib/categoryColors'
import { BrowseGridCard, BrowseListCard } from '../components/BrowseCard'
import AppActions from '../components/AppActions'

let categoryConfig: Record<string, CategoryStyle> = {}

/* ---- Main Component ---- */

export default function BrowseApps() {
  const [apps, setApps] = useState<BrowseApp[]>([])
  const [favorites, setFavorites] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('popular')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    return (localStorage.getItem('browse-view') as 'grid' | 'list') || 'grid'
  })

  useEffect(() => {
    localStorage.setItem('browse-view', viewMode)
  }, [viewMode])

  useEffect(() => {
    loadApps()
  }, [])

  async function loadApps() {
    try {
      const data = await browseApps()
      try {
        const cats = await listCategories()
        categoryConfig = buildCategoryConfig(cats)
      } catch {
        categoryConfig = {}
      }
      setApps(data)

      try {
        const favs = await listFavoriteApps()
        setFavorites(favs.map(f => f.name))
      } catch {
        // silently ignore
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load apps')
    } finally {
      setLoading(false)
    }
  }

  const toggleFavorite = useCallback(async (app: BrowseApp) => {
    if (app.my_permission == null) return // can't favorite apps you can't access
    const wasFavorited = app.is_favorited
    const updateFav = (a: BrowseApp) =>
      a.name === app.name ? { ...a, is_favorited: !wasFavorited } : a

    setApps(prev => prev.map(updateFav))
    if (wasFavorited) {
      setFavorites(prev => prev.filter(n => n !== app.name))
    } else {
      setFavorites(prev => [app.name, ...prev])
    }

    try {
      if (wasFavorited) {
        await unfavoriteApp(app.name)
      } else {
        await favoriteApp(app.name)
      }
    } catch {
      setApps(prev => prev.map(a =>
        a.name === app.name ? { ...a, is_favorited: wasFavorited } : a
      ))
      try {
        const favs = await listFavoriteApps()
        setFavorites(favs.map(f => f.name))
      } catch {
        // ignore
      }
    }
  }, [])

  const handleRequestAccess = useCallback(async (app: BrowseApp) => {
    // Optimistic update
    setApps(prev => prev.map(a =>
      a.name === app.name ? { ...a, has_pending_request: true } : a
    ))
    try {
      await requestAccess(app.name)
    } catch {
      // Revert on failure
      setApps(prev => prev.map(a =>
        a.name === app.name ? { ...a, has_pending_request: false } : a
      ))
    }
  }, [])

  function renderAppActions(app: BrowseApp) {
    if (app.my_permission == null) return undefined
    const permission = app.my_permission as 'view' | 'edit' | 'admin' | 'owner'
    return (
      <AppActions
        app={app as unknown as import('../lib/api').App}
        permission={permission}
        onStart={async () => { await startApp(app.name); await loadApps() }}
        onStop={async () => { await stopApp(app.name); await loadApps() }}
        onRebuild={async () => { await rebuildApp(app.name); await loadApps() }}
        size="sm"
      />
    )
  }

  // Featured: admin-curated apps
  const [showAllFeatured, setShowAllFeatured] = useState(false)

  const featuredApps = useMemo(() => {
    return apps.filter(a => a.is_featured)
  }, [apps])

  const visibleFeaturedApps = showAllFeatured ? featuredApps : featuredApps.slice(0, 4)

  // Category counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const app of apps) {
      if (app.category) {
        counts[app.category] = (counts[app.category] || 0) + 1
      }
    }
    return counts
  }, [apps])

  const favoriteSet = useMemo(() => new Set(favorites), [favorites])

  // Filtered apps for main grid
  const filteredApps = useMemo(() => {
    return apps
      .filter(app => {
        if (categoryFilter !== 'all' && app.category !== categoryFilter) return false
        if (showFavoritesOnly && !app.is_favorited && !favoriteSet.has(app.name)) return false
        if (search === '') return true
        const q = search.toLowerCase()
        return (
          (app.display_name || app.name).toLowerCase().includes(q) ||
          (app.description || '').toLowerCase().includes(q) ||
          (app.owner_name || app.owner_email).toLowerCase().includes(q)
        )
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name-asc':
            return (a.display_name || a.name).localeCompare(b.display_name || b.name)
          case 'name-desc':
            return (b.display_name || b.name).localeCompare(a.display_name || a.name)
          case 'recent':
            return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime()
          case 'newest':
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          default: // 'popular'
            return (b.visit_count_30d || 0) - (a.visit_count_30d || 0)
        }
      })
  }, [apps, search, sortBy, categoryFilter, showFavoritesOnly, favoriteSet])

  const hasActiveFilters = search !== '' || categoryFilter !== 'all' || showFavoritesOnly

  function clearAllFilters() {
    setSearch('')
    setCategoryFilter('all')
    setShowFavoritesOnly(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Browse</h1>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 text-sm">
          {error}
        </div>
      )}

      {apps.length === 0 ? (
        <div className="text-center py-16 rounded-xl border border-dashed border-border">
          <Search className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">No apps available yet</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            When users share their apps with the platform, they&apos;ll appear here.
          </p>
        </div>
      ) : (
        <>
          {/* Control bar */}
          <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-border">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search apps..."
                className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="pl-2.5 pr-7 py-1.5 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_6px_center] bg-no-repeat"
            >
              <option value="popular">Most Popular</option>
              <option value="recent">Recently Updated</option>
              <option value="newest">Newest First</option>
              <option value="name-asc">Name A &rarr; Z</option>
              <option value="name-desc">Name Z &rarr; A</option>
            </select>

            <button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors border ${
                showFavoritesOnly
                  ? 'bg-yellow-500/15 border-yellow-500/30 text-yellow-600'
                  : 'border-border text-muted-foreground hover:border-yellow-500/30 hover:text-yellow-600'
              }`}
            >
              <Heart className="w-3.5 h-3.5" fill={showFavoritesOnly ? 'currentColor' : 'none'} />
              Favorites
            </button>

            <div className="flex items-center border border-border rounded-lg overflow-hidden ml-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:text-foreground'
                }`}
                title="Grid view"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:text-foreground'
                }`}
                title="List view"
              >
                <List className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Category chips — color-coded */}
          <div className="flex flex-wrap items-center gap-1.5 mb-5">
            <button
              onClick={() => { setCategoryFilter('all'); setShowFavoritesOnly(false) }}
              className={`px-2.5 py-1 rounded border-l-2 text-xs transition-colors ${
                categoryFilter === 'all' && !showFavoritesOnly
                  ? 'bg-primary/10 border-l-primary text-primary font-medium'
                  : 'border border-border border-l-2 border-l-transparent text-muted-foreground hover:border-primary/30 hover:text-foreground'
              }`}
            >
              All
            </button>
            {Object.entries(categoryConfig).map(([key, cfg]) => {
              const count = categoryCounts[key] || 0
              if (count === 0) return null
              return (
                <button
                  key={key}
                  onClick={() => setCategoryFilter(categoryFilter === key ? 'all' : key)}
                  className={`px-2.5 py-1 rounded border-l-2 text-xs transition-colors ${
                    categoryFilter === key
                      ? `${cfg.bg} ${cfg.border} ${cfg.text} font-medium`
                      : 'border border-border border-l-2 border-l-transparent text-muted-foreground hover:border-primary/30 hover:text-foreground'
                  }`}
                >
                  {cfg.label} ({count})
                </button>
              )
            })}
          </div>

          {/* Featured section */}
          {featuredApps.length > 0 && !hasActiveFilters && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold flex items-center gap-1.5">
                  <BadgeCheck className="w-5 h-5 text-amber-500" />
                  Featured
                </h2>
                {featuredApps.length > 4 && (
                  <button
                    onClick={() => setShowAllFeatured(!showAllFeatured)}
                    className="text-sm text-primary hover:underline"
                  >
                    {showAllFeatured ? 'Show less' : `See all ${featuredApps.length}`}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {visibleFeaturedApps.map((app) => (
                  <BrowseGridCard
                    key={app.name}
                    app={app}
                    catCfg={app.category ? categoryConfig[app.category] : null}
                    onToggleFavorite={toggleFavorite}
                    badges={
                      app.access_level === 'specific' && (
                        <span title="Private"><Lock className="w-3 h-3 text-muted-foreground" /></span>
                      )
                    }
                    actions={renderAppActions(app) || <AccessButton app={app} onRequestAccess={handleRequestAccess} />}
                  />
                ))}
              </div>
            </div>
          )}

          {/* All Apps section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">All Apps</h2>
              <span className="text-sm text-muted-foreground">
                {filteredApps.length} {filteredApps.length === 1 ? 'item' : 'items'}
              </span>
            </div>

            {hasActiveFilters && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                <span>Showing {filteredApps.length} of {apps.length} apps</span>
                <button onClick={clearAllFilters} className="text-primary hover:underline">
                  Clear filters
                </button>
              </div>
            )}

            {filteredApps.length === 0 ? (
              <div className="text-center py-12 rounded-xl border border-dashed border-border">
                <Search className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                <h3 className="font-semibold mb-1">No apps match your filters</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Try a different search term or category.
                </p>
                <button onClick={clearAllFilters} className="text-primary hover:underline text-sm">
                  Clear all filters
                </button>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {filteredApps.map((app) => (
                  <BrowseGridCard
                    key={app.name}
                    app={app}
                    catCfg={app.category ? categoryConfig[app.category] : null}
                    onToggleFavorite={toggleFavorite}
                    badges={
                      app.access_level === 'specific' && (
                        <span title="Private"><Lock className="w-3 h-3 text-muted-foreground" /></span>
                      )
                    }
                    actions={renderAppActions(app) || <AccessButton app={app} onRequestAccess={handleRequestAccess} />}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredApps.map((app) => (
                  <BrowseListCard
                    key={app.name}
                    app={app}
                    catCfg={app.category ? categoryConfig[app.category] : null}
                    onToggleFavorite={toggleFavorite}
                    badges={
                      app.access_level === 'specific' && (
                        <span title="Private"><Lock className="w-3 h-3 text-muted-foreground" /></span>
                      )
                    }
                    actions={renderAppActions(app) || <AccessButton app={app} onRequestAccess={handleRequestAccess} />}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/* ---- Page-specific Sub-components ---- */

function AccessButton({ app, onRequestAccess }: { app: BrowseApp; onRequestAccess: (app: BrowseApp) => void }) {
  if (app.my_permission != null) {
    return null
  }

  if (app.has_pending_request) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-secondary text-muted-foreground">
        <Clock className="w-3 h-3" />
        Pending
      </span>
    )
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onRequestAccess(app) }}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
      title="Request Access"
    >
      <Lock className="w-3 h-3" />
      Request
    </button>
  )
}

